import { useCallback, useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { registerRuntimePushToken } from "../lib/pushClient";
import CareNotificationInbox from "./CareNotificationInbox";

export default function PlayerNotificationBell({ inboxPath = "/notifications", onClick = null, readOnly = false, compactDesktop = false }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const [count, setCount] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/notifications", { params: { unread_only: true } });
      setCount(data.unread_count || 0);
    } catch {}
  }, []);
  useEffect(() => {
    load(); registerRuntimePushToken().catch(() => {});
    window.addEventListener("focus", load);
    const syncReadState = (event) => setCount(Number(event.detail?.unreadCount || 0));
    window.addEventListener("pc:notifications-read", syncReadState);
    return () => {
      window.removeEventListener("focus", load);
      window.removeEventListener("pc:notifications-read", syncReadState);
    };
  }, [load]);
  const closePanel = useCallback((restoreFocus = true) => {
    setPanelOpen(false);
    if (restoreFocus) window.setTimeout(() => buttonRef.current?.focus(), 0);
  }, []);
  useEffect(() => {
    if (!panelOpen) return undefined;
    const outside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) closePanel(false);
    };
    const escape = (event) => { if (event.key === "Escape") closePanel(); };
    document.addEventListener("mousedown", outside);
    document.addEventListener("touchstart", outside);
    window.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("touchstart", outside);
      window.removeEventListener("keydown", escape);
    };
  }, [closePanel, panelOpen]);
  const openInbox = () => {
    if (compactDesktop && window.matchMedia?.("(min-width: 768px)")?.matches) {
      if (!readOnly) setPanelOpen((current) => !current);
      return;
    }
    if (onClick) return onClick();
    if (readOnly) return;
    nav(inboxPath, {
      state: { notificationOrigin: `${location.pathname}${location.search}` },
    });
  };
  const bell = <button ref={buttonRef} type="button" onClick={openInbox} disabled={readOnly} className="relative w-10 h-10 rounded-full grid place-items-center" style={{ background: "var(--pc-charcoal)", border: "1px solid var(--pc-border)", color: "var(--pc-muted)", opacity: readOnly ? 0.5 : 1 }} aria-label={t("notifications")} aria-expanded={compactDesktop ? panelOpen : undefined} aria-controls={compactDesktop ? "care-notification-popover" : undefined} data-testid="notification-bell"><Bell size={18} />{count > 0 && <span className="absolute -right-1 -top-1 min-w-5 h-5 px-1 rounded-full grid place-items-center text-[10px] font-bold" style={{ background: "var(--pc-cyan)", color: "#001014", boxShadow: "0 0 12px var(--pc-cyan-dim)" }} data-testid="notification-badge">{count > 99 ? "99+" : count}</span>}</button>;
  if (!compactDesktop) return bell;
  return <div ref={wrapperRef} className="relative shrink-0">
    {bell}
    {panelOpen && <div id="care-notification-popover" role="dialog" aria-label={t("notifications")} className="absolute right-0 top-12 z-50 w-[min(420px,calc(100vw-2rem))]" style={{ boxShadow: "0 22px 70px rgba(0,0,0,.42)" }} data-testid="care-notification-popover">
      <CareNotificationInbox compact fallbackPath={inboxPath.split("?")[0] || "/care"} onClose={() => closePanel()} onNavigate={(path) => { closePanel(false); nav(path); }} />
    </div>}
  </div>;
}
